/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useCallback, useState, useContext } from 'react'
import { useServiceContext } from '../../../root'
import { getAllForTable } from '../../../services/api/metadata'
import { RcmTableDataWrapper } from '../../../components/rcm-table/RcmTableDataWrapper'
import { RcmTableDataContext } from '../../../components/rcm-table/RcmTableDataContext'
import RcmTableFilterTrigger from '../../../components/rcm-table/RcmTableFilterTrigger'
import Loader from '../../../components/shared/Loader'
import PageMenu from './PageMenu'
import TableFilter from './TableFilter'
import Table from './Table'
import ModalEdit from './Actions/ModalEdit'

const PropertiesContent: React.FC = () => {
  const tableContext = useContext(RcmTableDataContext)
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [editingProperty, setEditingProperty] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const handleOpenModal = (property?: any) => {
    setEditingProperty(property || null)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingProperty(null)
  }

  return (
    <>
      <Loader blocking={isExporting}>
        <div className="row">
          <div className="col-xs-12">
            <div className="d-flex flex-wrap" style={{ paddingBottom: '1%' }}>
              <h1 className="align-left align-top" id="d16f2f31-e4ba-4ffc-8367-63fd9eeded72" style={{ margin: '0' }}>
                {translate('METADATA_TITLE')}
              </h1>

              <div className="align-right align-bottom d-flex">
                <PageMenu
                  onAddProperty={() => handleOpenModal()}
                  onExportStart={() => setIsExporting(true)}
                  onExportEnd={() => setIsExporting(false)}
                />
                <RcmTableFilterTrigger />
              </div>
            </div>

            <TableFilter />
            <Table
              onEditProperty={handleOpenModal}
              onDeleteSuccess={() => tableContext?.refreshTable()}
              onExportStart={() => setIsExporting(true)}
              onExportEnd={() => setIsExporting(false)}
            />
          </div>
        </div>
        {isModalOpen && <ModalEdit property={editingProperty || undefined} onClose={handleCloseModal} />}
      </Loader>
    </>
  )
}

const PropertiesPage: React.FC = () => {
  // Create loadData function for RcmTableDataWrapper
  const loadData = useCallback(async (pagingParams: any, filter: any) => {
    try {
      const metadataIds = filter?.metadatas ? filter.metadatas.map((m: any) => m.id || m.value || m) : []
      const result = await getAllForTable({
        pageStart: pagingParams.pageStart || 0,
        pageSize: pagingParams.pageSize || 15,
        sortBy: pagingParams.sortBy || 'NAME',
        sortDesc: pagingParams.sortDesc !== undefined ? pagingParams.sortDesc : false,
        _noLoadingSpinner: true,
        ...(metadataIds.length > 0 ? { id: metadataIds } : {})
      })
      return {
        elements: result.elements || [],
        totalElements: result.totalElements || 0
      }
    } catch (error) {
      return {
        elements: [],
        totalElements: 0
      }
    }
  }, [])

  return (
    <div className="mcc-content-frame theme-sandman">
      <RcmTableDataWrapper
        loadData={loadData}
        defaultSortBy="NAME"
        defaultSortDesc={false}
        persistenceKey="settingsProperties"
        doPreload={true}
      >
        <PropertiesContent />
      </RcmTableDataWrapper>
    </div>
  )
}

export default PropertiesPage
